import { unlockProjectData } from '../../shared/functions/fetchProject.js'
import {
  buildLoginRedirectUrl,
  isTrustedHandoffTarget
} from '../../shared/functions/authHandoff.js'
import { workspaceOrigin } from '../../shared/functions/canvasUrl.js'
import { sha256 } from '../../shared/functions/crypto.js'

// PreviewErrorBoundary — rendered whenever getProjectData() throws.
// Branches on `fetchError.code`:
//   project_not_public         → legacy "no env published" copy + env picker
//   project_private            → "Sign in to view" CTA (cross-domain handoff)
//   project_password_protected → password card (anon unlock via unlockProjectData)
//   <anything else>            → generic error
// Uses only design-system tokens (no raw px / hex / rem).

const _redirectToWorkspaceLogin = () => {
  if (typeof window === 'undefined') return
  const here = window.location.href
  // Target hardcoded to the trusted preview surface — defensive even
  // though we already gate on `isTrustedHandoffTarget` server-side in
  // signin.js. Caller would have to actively misconfigure to reach an
  // untrusted host.
  if (!isTrustedHandoffTarget(here)) {
    console.warn('[preview/auth] current URL not a trusted handoff target')
  }
  const url = buildLoginRedirectUrl(workspaceOrigin(), here)
  window.location.replace(url)
}

export const PreviewErrorBoundary = {
  flow: 'column',
  align: 'center center',
  width: '100%',
  height: '100%',
  background: 'document',
  padding: 'D',
  gap: 'C',
  position: 'relative',

  // Brand placeholder that replaces PreviewNavbar in the error state.
  // Pinned to top-center of the viewport — independent of the centered
  // content column below. Click routes to https://symbols.app.
  Logo: {
    extends: 'Logo',
    tag: 'a',
    attr: { href: 'https://symbols.app' },
    position: 'absolute',
    top: 'B',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: '2',
    cursor: 'pointer'
  },

  Inner: {
    flow: 'column',
    align: 'center center',
    gap: 'B',
    maxWidth: 'H2',
    width: '100%',

    IconWrapper: {
      align: 'center center',
      boxSize: 'E E',
      round: 'D',
      background: (el, s) => {
        const code = s.root?.fetchError?.code
        if (code === 'project_private') return 'blue.05'
        if (code === 'project_password_protected') return 'blue.05'
        return 'red.05'
      },

      Icon: {
        extends: 'Icon',
        // `warning` was never published in the brand sprite, so the
        // generic-error fallback rendered an empty placeholder. Use
        // `xCircleFill` (registered in `company/packages/brand/
        // designSystem/icons.js`) so failed-fetch states actually show
        // an icon.
        icon: (el, s) => {
          const code = s.root?.fetchError?.code
          if (code === 'project_private') return 'lock'
          if (code === 'project_password_protected') return 'lock'
          return 'xCircleFill'
        },
        boxSize: 'C C',
        color: (el, s) => {
          const code = s.root?.fetchError?.code
          if (code === 'project_private') return 'blue'
          if (code === 'project_password_protected') return 'blue'
          return 'red'
        }
      }
    },

    Heading: {
      flow: 'column',
      align: 'center center',
      gap: 'X2',
      textAlign: 'center',

      Title: {
        tag: 'h2',
        text: (el, s) => {
          const err = s.root?.fetchError
          if (!err) return 'Preview unavailable'
          if (err.code === 'project_private') {
            return err.signedIn ? 'No access to this project' : 'Sign in to view'
          }
          if (err.code === 'project_password_protected') return 'Password required'
          if (err.code === 'project_not_public') return 'Project not published'
          return 'Preview failed to load'
        },
        fontSize: 'D',
        fontWeight: '600',
        color: 'title',
        lineHeight: '1.3'
      },

      Subtitle: {
        tag: 'p',
        text: (el, s) => {
          const err = s.root?.fetchError
          if (!err) return 'An unexpected error occurred.'
          const owner = err.owner || ''
          const key = err.key || ''
          const slug = (owner && key) ? `${owner}/${key}` : 'this project'
          if (err.code === 'project_private') {
            return err.signedIn
              ? `You don't have access to ${slug}. Switch accounts or ask the owner for an invite.`
              : `${slug} is private. Sign in with an account that has access.`
          }
          if (err.code === 'project_password_protected') {
            return `${slug} is password-protected. Enter the password to view.`
          }
          if (owner && key) return slug
          return 'Unable to load project data.'
        },
        fontSize: 'Z1',
        color: 'caption',
        textAlign: 'center'
      }
    },

    // Generic error / not-public path — keeps the legacy details block.
    // Hidden for private + password-protected since the heading + CTA
    // already explain the state.
    ErrorDetail: {
      hide: (el, s) => {
        const code = s.root?.fetchError?.code
        return code === 'project_private' || code === 'project_password_protected'
      },
      flow: 'column',
      gap: 'X',
      width: '100%',
      padding: 'A B',
      background: 'overlay',
      round: 'C',
      border: '1px solid',
      borderColor: 'line',

      CodeLine: {
        tag: 'code',
        text: (el, s) => {
          const err = s.root?.fetchError
          if (!err) return 'Unknown error'
          const code = err.code ? `[${err.code}] ` : ''
          return `${code}${err.message || 'Unknown error'}`
        },
        fontSize: 'Z2',
        color: 'red',
        fontFamily: 'monospace',
        wordBreak: 'break-all'
      },

      TriedEnvs: {
        hide: (el, s) => {
          const err = s.root?.fetchError
          return !err?.triedEnvs?.length
        },
        flow: 'row',
        gap: 'X',
        align: 'center start',
        flexWrap: 'wrap',

        Label: {
          text: 'Tried:',
          fontSize: 'Z2',
          color: 'paragraph',
          lineHeight: '1.6'
        },

        EnvChips: {
          flow: 'row',
          gap: 'X',
          flexWrap: 'wrap',
          children: (el, s) => {
            const envs = s.root?.fetchError?.triedEnvs || []
            return envs.map(e => ({ key: e, text: e }))
          },
          childrenAs: 'state',
          childExtends: 'Chip',
          childProps: {
            fontSize: 'Z2',
            padding: 'W X',
            round: 'B',
            background: 'overlay',
            color: 'paragraph',
            text: (el, s) => s.text || s.key
          }
        }
      }
    },

    // Private project — sign-in CTA. The button bounces through
    // my.symbols.app/signin?redirect=<here>; signin.js will mint an
    // auth-handoff fragment and replace location back here.
    PrivateActions: {
      hide: (el, s) => s.root?.fetchError?.code !== 'project_private',
      flow: 'row',
      gap: 'X',
      align: 'center center',
      flexWrap: 'wrap',
      width: '100%',

      SignInButton: {
        extends: 'Button',
        text: (el, s) => s.root?.fetchError?.signedIn ? 'Switch account' : 'Sign in',
        theme: 'primary',
        padding: 'Y2 B',
        round: 'C',
        fontSize: 'Z1',
        fontWeight: '500',
        minHeight: 'B',
        cursor: 'pointer',
        align: 'center center',
        onClick: () => _redirectToWorkspaceLogin()
      },

      OpenInEditorButton: {
        extends: 'Link',
        tag: 'a',
        href: (el, s) => {
          const err = s.root?.fetchError
          const owner = err?.owner || ''
          const key = err?.key || ''
          if (!owner || !key) return 'https://edit.symbols.app'
          return `https://edit.symbols.app/${owner}/${key}`
        },
        target: '_blank',
        text: 'Open in editor',
        theme: 'secondary',
        padding: 'Y2 B',
        round: 'C',
        fontSize: 'Z1',
        fontWeight: '500',
        minHeight: 'B',
        cursor: 'pointer',
        align: 'center center'
      }
    },

    // Password-protected project — inline form. On submit, hash the
    // value, POST to /unlock, and replace state.data on success.
    // Inline form state lives on a sub-state to keep the unlock flow
    // self-contained — root state only flips when the unlock succeeds.
    PasswordCard: {
      hide: (el, s) => s.root?.fetchError?.code !== 'project_password_protected',
      tag: 'form',
      flow: 'column',
      gap: 'A',
      width: '100%',
      padding: 'B',
      background: 'overlay',
      round: 'C',
      border: '1px solid',
      borderColor: 'line',
      state: {
        loading: false,
        errorMessage: ''
      },
      onSubmit: async (ev, el, s) => {
        ev.preventDefault()
        const root = el.node
        const value = root.querySelector('input[name="password"]')?.value || ''
        if (!value) {
          s.update({ errorMessage: 'Password is required.' })
          return
        }
        s.update({ loading: true, errorMessage: '' })
        try {
          const err = s.root?.fetchError
          const hashed = await sha256(value)
          const data = await unlockProjectData({
            owner: err?.owner,
            key: err?.key,
            envKey: err?.passwordEnv || (err?.triedEnvs?.[0]) || 'production',
            password: hashed
          })
          if (!data) {
            s.update({ loading: false, errorMessage: 'Incorrect password. Try again.' })
            return
          }
          // Swap the root state so PreviewNavbar + Scene re-read and
          // the boundary hides itself (its parent's `hide` watches
          // `s.root?.fetchError`).
          s.root.update({
            data,
            fetchError: null,
            resolvedEnv: data._resolvedEnv,
            requestedEnv: data._requestedEnv,
            usedFallback: false,
            env: data._resolvedEnv
          })
        } catch (e) {
          s.update({ loading: false, errorMessage: e?.message || 'Failed to unlock.' })
        }
      },

      PasswordLabel: {
        tag: 'label',
        // `for` is a reserved word in JS — DOMQL surfaces it via the
        // `attr` escape hatch (matches AppLoginCard's pattern).
        attr: { for: 'preview-password' },
        fontSize: 'Z2',
        fontWeight: '500',
        color: 'title',
        text: 'Password'
      },

      PasswordInput: {
        tag: 'input',
        id: 'preview-password',
        type: 'password',
        name: 'password',
        autocomplete: 'current-password',
        placeholder: 'Enter project password',
        width: '100%',
        padding: 'Y2 A',
        round: 'B',
        background: 'document',
        border: '1px solid',
        borderColor: 'line',
        color: 'title',
        fontSize: 'Z1',
        boxSizing: 'border-box',
        ':focus-visible': {
          outline: 'none',
          borderColor: 'blue',
          boxShadow: '0 0 0 2px var(--color-blue-05)'
        },
        // Clear any previous "incorrect password" message as the user
        // starts typing again — the form will re-validate on submit.
        onInput: (ev, el, s) => {
          if (s.errorMessage) s.update({ errorMessage: '' })
        }
      },

      ErrorLine: {
        hide: (el, s) => !s.errorMessage,
        tag: 'p',
        text: (el, s) => s.errorMessage,
        fontSize: 'Z2',
        color: 'red',
        margin: '0'
      },

      SubmitRow: {
        flow: 'row',
        gap: 'X',
        align: 'center center',

        UnlockButton: {
          extends: 'Button',
          tag: 'button',
          type: 'submit',
          text: (el, s) => s.loading ? 'Unlocking…' : 'Unlock',
          theme: 'primary',
          padding: 'Y2 B',
          round: 'C',
          fontSize: 'Z1',
          fontWeight: '500',
          minHeight: 'B',
          cursor: 'pointer',
          align: 'center center',
          flex: '1',
          disabled: (el, s) => !!s.loading
        },

        SignInLink: {
          extends: 'Link',
          tag: 'button',
          type: 'button',
          text: 'Sign in instead',
          theme: 'transparent',
          padding: 'Y2 B',
          round: 'C',
          fontSize: 'Z1',
          color: 'paragraph',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          onClick: () => _redirectToWorkspaceLogin()
        }
      }
    },

    // Two rows of actions, each with internally-consistent button sizing.
    // Row 1 — primary actions (Open in editor, Retry). Theme-tinted.
    // Row 2 — env-switch picker (Label + 3 chip buttons). Smaller chip
    //         sizing is intentional (these are quick-toggle pills, not
    //         primary CTAs) but ALL three chips are identical to each other.
    Actions: {
      hide: (el, s) => {
        const code = s.root?.fetchError?.code
        return code === 'project_private' || code === 'project_password_protected'
      },
      flow: 'column',
      gap: 'A',
      align: 'center center',
      width: '100%',

      // Shared sizing token applied to both primary actions so they
      // visually match exactly (was: same `padding: 'A B'` but different
      // `extends` gave different intrinsic heights).
      PrimaryRow: {
        flow: 'row',
        gap: 'X',
        align: 'center center',
        flexWrap: 'wrap',

        OpenInEditor: {
          extends: 'Link',
          tag: 'a',
          href: (el, s) => {
            const err = s.root?.fetchError
            const owner = err?.owner || ''
            const key = err?.key || ''
            if (!owner || !key) return 'https://edit.symbols.app'
            const env = err?.triedEnvs?.[0] || 'development'
            return `https://edit.symbols.app/${owner}/${key}?env=${env}`
          },
          target: '_blank',
          text: 'Open in editor',
          theme: 'primary',
          padding: 'Y2 B',
          round: 'C',
          fontSize: 'Z1',
          fontWeight: '500',
          minHeight: 'B',
          cursor: 'pointer',
          align: 'center center'
        },

        RetryButton: {
          extends: 'Button',
          text: 'Retry',
          theme: 'secondary',
          padding: 'Y2 B',
          round: 'C',
          fontSize: 'Z1',
          fontWeight: '500',
          minHeight: 'B',
          cursor: 'pointer',
          align: 'center center',
          onClick: (ev, el) => {
            window.location.reload()
          }
        }
      },

      // Row 2 — env switcher. Label + identically-sized chips.
      SwitchEnvButtons: {
        flow: 'row',
        gap: 'X',
        align: 'center center',
        flexWrap: 'wrap',

        Label: {
          text: 'Try env:',
          fontSize: 'Z2',
          color: 'paragraph'
        },

        Envs: {
          flow: 'row',
          gap: 'X',
          align: 'center center',
          children: [
            { key: 'development', text: 'Dev' },
            { key: 'staging', text: 'Staging' },
            { key: 'production', text: 'Prod' }
          ],
          childrenAs: 'state',
          childExtends: 'Button',
          childProps: {
            text: (el, s) => s.text || s.key,
            theme: 'secondary',
            padding: 'W A',
            round: 'C',
            fontSize: 'Z2',
            fontWeight: '500',
            minHeight: 'A2',
            cursor: 'pointer',
            align: 'center center',
            onClick: (ev, el, s) => {
              if (!s?.key) return
              const url = new URL(window.location.href)
              url.searchParams.set('env', s.key)
              window.location.href = url.toString()
            }
          }
        }
      }
    }
  }
}
